import React from "react";
import "./legal.css";

const TermsOfService = () => {
  return (
    <div className="legal-container">
<h1>Terms of Service</h1>

<p>
  These Terms of Service (“Terms”) govern your access to and use of the web site,
  web application, and related services provided by <strong>ZenQB EduTech LLP</strong> (“ZenQB”, “we”,
  “us” or “our”). By accessing or using the Platform, you agree to be bound by
  these Terms. If you do not agree to these Terms, please refrain from using the
  Platform.
</p>

<h2>1. Definitions</h2>

<p>
  <strong>Platform</strong> means the ZenQB website, web application, services,
  features, content, and any associated offline components.
</p>

<p>
  <strong>User(s)</strong> means any individual accessing or using the Platform.
</p>

<p>
  <strong>Content</strong> means questions, explanations, text, images,
  multimedia, and other materials provided through the Platform.
</p>

<p>
  <strong>Services</strong> means the features made available on the Platform,
  including question banks, AI-assisted explanations, and custom paper generation.
</p>

<h2>2. Applicability</h2>

<p>
  These Terms apply to all Users of the Platform. Any additional terms that apply
  to particular services or paid subscriptions are set out in Section 8
  (Subscription, Payments and Refunds) and in any separate purchase or
  subscription agreement.
</p>

<h2>3. Accounts and Registration</h2>

<p>
  3.1 Account creation may be required to access certain features. You agree to
  provide accurate, current, and complete information during registration and to
  update such information as necessary.
</p>

<p>
  3.2 Eligibility: by creating an account, you represent and warrant that you
  meet applicable legal capacity requirements to enter into this agreement. If
  you are under 18, you represent that you are using the Platform with the
  consent and supervision of a parent or legal guardian.
</p>

<p>
  3.3 You are responsible for maintaining the confidentiality of your account
  credentials and for all activity that occurs under your account. Notify us
  immediately of any unauthorized access or other security breach.
</p>

<p>
  3.4 ZenQB reserves the right to refuse registration, suspend, or terminate
  accounts in its sole discretion where these Terms are violated or for
  administrative reasons.
</p>

<h2>4. Permitted Use; Prohibited Conduct</h2>

<p>
  4.1 <strong>Permitted Use.</strong> The Platform is provided to individual
  learners for personal, educational use and internal evaluation.
</p>

<p>
  4.2 <strong>Prohibited Conduct.</strong> You shall not, without ZenQB’s prior
  written consent:
</p>

<ul>
  <li>
    copy, reproduce, distribute, publish, sell, license, or create derivative
    works from Content for any commercial purpose;
  </li>
  <li>
    attempt to circumvent or defeat technical or access controls (including
    scraping, harvesting, or mass downloading);
  </li>
  <li>
    interfere with the security, integrity, availability, or performance of the
    Platform;
  </li>
  <li>
    use the Platform to infringe the intellectual property rights of third
    parties or to facilitate academic dishonesty; or
  </li>
  <li>
    upload or transmit viruses, malware, or other harmful code.
  </li>
</ul>

<p>
  4.3 ZenQB may investigate and take any available remedial action (including
  removing Content, suspending accounts, and pursuing legal remedies) if you
  breach these obligations.
</p>

<h2>5. Content, Accuracy and AI-Generated Explanations</h2>

<p>
  5.1 <strong>Content Provided “AS IS.”</strong> Content and AI-assisted
  explanations are provided for educational purposes only and on an “as is”
  basis. While we endeavour to maintain the quality and accuracy of Content,
  ZenQB does not guarantee that Content will be complete, accurate, current, or
  suitable for any specific examination or syllabus.
</p>

<p>
  5.2 <strong>AI Assistance.</strong> AI-powered explanations are intended to
  support learning and may be imperfect. Users should exercise judgment and
  cross-check critical information against authoritative sources (textbooks,
  official syllabi, or instructors).
</p>

<p>
  5.3 <strong>User Content.</strong> If you upload or submit content to the
  Platform (including question sets, notes, or feedback), you grant ZenQB a
  non-exclusive, royalty-free, worldwide license to use, reproduce, modify,
  distribute, and display such content to provide and improve the Platform,
  subject to our Privacy Policy.
</p>

<h2>6. Intellectual Property</h2>

<p>
  6.1 <strong>Ownership.</strong> All intellectual property rights in the
  Platform and Content are owned or licensed by ZenQB or its licensors. Except
  for the limited license expressly granted herein, nothing in these Terms shall
  be construed as transferring any ownership rights to Users.
</p>

<p>
  6.2 <strong>Limited License.</strong> Subject to compliance with these Terms,
  ZenQB grants you a limited, non-exclusive, non-transferable, revocable license
  to access and use the Platform for your personal education and practice.
</p>

<h2>7. Custom Question Papers and Generated Content</h2>

<p>
  7.1 <strong>Use.</strong> Tools that produce custom question papers are
  provided for personal practice only. Redistribution, resale, or public
  sharing of generated papers is prohibited without prior written consent.
</p>

<p>
  7.2 <strong>No Exam Guarantees.</strong> Use of generated materials does not
  guarantee performance or exam results.
</p>

<h2>8. Subscription, Payments and Refunds</h2>

<p>
  8.1 <strong>Subscriptions.</strong> If ZenQB offers paid subscriptions or paid
  content, the pricing, payment terms, and subscription features will be
  displayed at the point of sale and governed by any separate subscription
  agreement.
</p>

<p>
  8.2 <strong>Billing and Renewal.</strong> Subscriptions may automatically
  renew unless cancelled in accordance with the subscription terms. You
  authorize ZenQB to charge the payment method provided for fees related to your
  subscription.
</p>

<p>
  8.3 <strong>Refunds.</strong> Unless otherwise specified at the point of
  purchase, payments are non-refundable. Any exceptions will be governed by
  ZenQB’s refund policy, if published.
</p>

<h2>9. Security, Privacy and Data Use</h2>

<p>
  9.1 <strong>Privacy.</strong> Collection, use, and retention of personal data
  are governed by ZenQB’s Privacy Policy. By using the Platform you consent to
  such data practices.
</p>

<p>
  9.2 <strong>Security.</strong> We implement reasonable administrative,
  technical, and physical measures to protect user data, but we cannot guarantee
  absolute security. You remain responsible for safeguarding your account
  credentials.
</p>

<h2>10. Availability and Updates</h2>

<p>
  10.1 <strong>Maintenance and Changes.</strong> ZenQB may update, suspend, or
  discontinue any aspect of the Platform (including Content and features)
  without liability. We will endeavour to provide notice of significant changes
  where practicable.
</p>

<p>
  10.2 <strong>Backups and Access.</strong> Users are responsible for retaining
  copies of any content they create or upload. ZenQB is not responsible for any
  loss of data.
</p>

<h2>11. Suspension and Termination</h2>

<p>
  11.1 <strong>Termination.</strong> ZenQB may suspend or terminate access to the
  Platform for breach of these Terms, requests from law enforcement, or where
  continued access would harm the Platform or other users.
</p>

<p>
  11.2 <strong>Effect of Termination.</strong> On termination, your right to use
  the Platform will cease. Sections that by their nature survive termination
  (including Intellectual Property, Disclaimers, Limitations of Liability, and
  Indemnity) shall continue in effect.
</p>

<h2>12. Disclaimers and Warranties</h2>

<p>
  12.1 <strong>No Warranty.</strong> EXCEPT AS EXPRESSLY PROVIDED HEREIN, THE
  PLATFORM AND CONTENT ARE PROVIDED WITHOUT WARRANTY OF ANY KIND, WHETHER
  EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, INCLUDING ANY IMPLIED WARRANTIES OF
  MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
</p>

<h2>13. Limitation of Liability</h2>

<p>
  13.1 TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ZENQB, ITS AFFILIATES,
  AND LICENSORS SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL,
  SPECIAL, OR PUNITIVE DAMAGES, OR LOSS OF PROFITS, REVENUE, DATA, OR BUSINESS
  ARISING FROM OR RELATED TO THIS AGREEMENT. ZENQB’S AGGREGATE LIABILITY FOR
  DIRECT DAMAGES SHALL NOT EXCEED THE AMOUNT PAID BY YOU TO ZENQB DURING THE
  TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO LIABILITY (OR, IF NO
  AMOUNT WAS PAID, INR 5,000).
</p>

<h2>14. Indemnity</h2>

<p>
  14.1 You agree to indemnify, defend, and hold harmless ZenQB and its officers,
  partners, employees, and agents from any claims, liabilities, damages, losses,
  costs, and expenses (including reasonable legal fees) arising out of your
  violation of these Terms, your use of the Platform, or your infringement of
  third-party rights.
</p>

<h2>15. Governing Law and Dispute Resolution</h2>

<p>
  15.1 <strong>Governing Law.</strong> These Terms shall be governed by and
  construed in accordance with the laws of India.
</p>

<p>
  15.2 <strong>Dispute Resolution.</strong> Except where prohibited by applicable
  law, disputes shall be subject to the exclusive jurisdiction of the courts of Bangalore, Karnataka.
</p>

<h2>16. Changes to Terms</h2>

<p>
  16.1 ZenQB may modify these Terms at any time. Revised Terms will be published
  with an updated “Last updated” date. Continued use of the Platform after such
  changes constitutes acceptance of the revised Terms.
</p>

<h2>17. Miscellaneous</h2>

<p>
  17.1 <strong>Relationship.</strong> Nothing in these Terms creates a
  partnership, joint venture, employment, or agency relationship between you
  and ZenQB.
</p>

<p>
  17.2 <strong>Severability.</strong> If any provision is held invalid or
  unenforceable, the remaining provisions will remain in full force and effect.
</p>

<p>
  17.3 <strong>Waiver.</strong> Failure to enforce any right or provision of
  these Terms will not constitute a waiver of such right or provision.
</p>

<h2>18. Contact</h2>

<p>
  If you have questions about these Terms, please contact:
</p>

<p>
  <strong>ZenQB EduTech LLP</strong><br />
  Email: <strong>support@zenqb.com</strong><br />
</p>

      <p className="muted">Last updated: February 2026</p>
    </div>
  );
};

export default TermsOfService;
